import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Accordion,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { useCombobox } from "downshift";

import { searchSurgeryProviders } from "@services/cat-surgery-providers";
import {
  addCaseSurgeryProvider,
  getCaseSurgeryProviders,
} from "@services/case-surgery-providers";
import { update } from "@services/hospitalization";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 250;

export const SurgeryProvidersSection = ({ disabled = true, ...props }) => {
  return (
    <Card>
      <Card.Header>
        <Accordion.Toggle
          as={Button}
          variant="link"
          eventKey={props.eventKey}
          disabled={disabled}
        >
          Proveedores de Cirugia
        </Accordion.Toggle>
        <i
          className={`fa ${props.icon}`}
          style={{ position: "absolute", right: 0, marginRight: "15px", marginTop: "10px" }}
        />
      </Card.Header>
      <Accordion.Collapse eventKey={props.eventKey}>
        <Card.Body>
          <SurgeryProvidersContent {...props} />
        </Card.Body>
      </Accordion.Collapse>
    </Card>
  );
};

const SurgeryProvidersContent = (props) => {
  const caseId = props.data?.id;
  const hospitalEst = props.data?.hospital?.hospitalEst;
  const initialRequires = props.data?.requiresSurgeryProviders;

  const [requires, setRequires] = useState(
    initialRequires === "Si" ? true : initialRequires === "No" ? false : null
  );
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!caseId) return;
    getCaseSurgeryProviders(caseId).then((r) => setRows(r.data || []));
  }, [caseId]);

  useEffect(() => {
    if (initialRequires === "Si") setRequires(true);
    else if (initialRequires === "No" || initialRequires === null || initialRequires === undefined)
      setRequires(initialRequires === "No");
  }, [initialRequires]);

  const persistRequires = (nextValue) => {
    const stringValue = nextValue ? "Si" : "No";
    window.requires_surgery_providers = stringValue;
    if (caseId) {
      update({ requiresSurgeryProviders: stringValue }, caseId);
    }
  };

  const refreshRows = () => {
    if (!caseId) return;
    getCaseSurgeryProviders(caseId).then((r) => setRows(r.data || []));
  };

  const handleSaved = () => {
    refreshRows();
  };

  return (
    <>
      <Alert variant="dark" className="text-center" style={{ fontSize: "14px" }}>
        PROVEEDORES DE CIRUGIA
      </Alert>

      <Card>
        <Card.Body>
          <Row>
            <Col>
              <Form.Group controlId="surgeryProviderRequires">
                <Form.Label>SOLICITUD CON PROVEEDOR DE CIRUGIA *</Form.Label>
                <div className="mb-3">
                  <Form.Check
                    custom
                    inline
                    label="SI"
                    type="radio"
                    value="Si"
                    id="surgeryProviderRequiresSi"
                    disabled={props.disabledAll}
                    onChange={() => {
                      setRequires(true);
                      persistRequires(true);
                    }}
                    checked={requires === true}
                    name="surgeryProviderRequires"
                  />
                  <Form.Check
                    custom
                    inline
                    label="NO"
                    type="radio"
                    value="No"
                    id="surgeryProviderRequiresNo"
                    disabled={props.disabledAll}
                    onChange={() => {
                      setRequires(false);
                      persistRequires(false);
                    }}
                    checked={requires === false}
                    name="surgeryProviderRequires"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <i className="fa fa-question-circle h2 mr-2" />
              NOTA: ESTIMADO HOSPITAL, FAVOR DE AGREGAR SOLO LOS PROVEEDORES QUE HAYAN INTERVENIDO
              EN PROCEDIMIENTOS DE CARDIOLOGIA Y ORTOPEDIA SI SE PRESENTO DENTRO DE LA
              HOSPITALIZACION DEL ASEGURADO.
            </Col>
          </Row>

          {requires && (
            <SurgeryProviderForm
              caseId={caseId}
              hospitalEst={hospitalEst}
              disabledAll={props.disabledAll}
              submitting={submitting}
              setSubmitting={setSubmitting}
              onSaved={handleSaved}
            />
          )}

          <Table responsive striped bordered hover className="mt-4">
            <thead>
              <tr>
                <th>NOMBRE DEL PROVEEDOR</th>
                <th>USUARIO</th>
                <th>FECHA HORA (DD/MM/AAAA HH:MM:SS)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.nombreProveedor}</td>
                  <td>{row.username}</td>
                  <td>
                    {row.createdAt
                      ? moment(row.createdAt).format("DD/MM/YYYY hh:mm:ss A")
                      : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

const SurgeryProviderForm = ({ caseId, hospitalEst, disabledAll, submitting, setSubmitting, onSaved }) => {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef();

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    highlightedIndex,
    inputValue,
    reset,
  } = useCombobox({
    items: options,
    itemToString: (item) => (item ? item.nombreProveedor : ""),
    onInputValueChange: ({ inputValue: next }) => {
      if (selected && next !== selected.nombreProveedor) {
        setSelected(null);
      }
      const value = (next || "").trim();
      clearTimeout(debounceRef.current);
      if (value.length < MIN_SEARCH_LENGTH) {
        setOptions([]);
        return;
      }
      debounceRef.current = setTimeout(() => {
        searchSurgeryProviders(value, hospitalEst)
          .then((r) => setOptions(r.data || []))
          .catch(() => setOptions([]));
      }, SEARCH_DEBOUNCE_MS);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      setSelected(selectedItem || null);
    },
  });

  const handleSubmit = () => {
    const nombre = (inputValue || "").trim();
    if (!nombre || !caseId) return;
    setSubmitting(true);
    addCaseSurgeryProvider({
      caseId,
      catSurgeryProviderId: selected?.id || null,
      nombreProveedor: nombre,
    })
      .then(() => {
        reset();
        setSelected(null);
        setOptions([]);
        onSaved();
      })
      .finally(() => setSubmitting(false));
  };

  const placeholder = "No se encontró ningún registro, favor de Agregar el Nombre del Proveedor";

  return (
    <Row className="align-items-end">
      <Col md={9}>
        <Form.Group controlId="surgeryProviderName">
          <Form.Label>NOMBRE DEL PROVEEDOR *</Form.Label>
          <div {...getComboboxProps()} style={{ position: "relative" }}>
            <Form.Control
              {...getInputProps({
                placeholder,
                disabled: disabledAll,
                autoComplete: "off",
              })}
            />
            {isOpen && options.length > 0 && (
              <ListGroup
                {...getMenuProps()}
                style={{
                  position: "absolute",
                  zIndex: 10,
                  top: "100%",
                  left: 0,
                  right: 0,
                  maxHeight: 240,
                  overflowY: "auto",
                  marginTop: 2,
                }}
              >
                {options.map((item, index) => (
                  <ListGroup.Item
                    key={item.id}
                    {...getItemProps({ item, index })}
                    active={highlightedIndex === index}
                    action
                    style={{ cursor: "pointer" }}
                  >
                    {item.nombreProveedor}
                    {item.especialidad ? (
                      <small className="text-muted ml-2">{item.especialidad}</small>
                    ) : null}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {/* keep menu accessible even when closed */}
            {!isOpen && <ul {...getMenuProps()} style={{ display: "none" }} />}
          </div>
        </Form.Group>
      </Col>
      <Col md={3}>
        <Form.Group>
          <Button
            variant="primary"
            type="button"
            onClick={handleSubmit}
            disabled={submitting || disabledAll || !(inputValue || "").trim()}
          >
            GUARDAR
          </Button>
        </Form.Group>
      </Col>
    </Row>
  );
};
